import React, { useState, useRef, useEffect } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';

const FoundItemsDashboard = ({ foundItems = [] }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [checkedRows, setCheckedRows] = useState([]);
  const [dividerPosition, setDividerPosition] = useState(50); // Percentage of width
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const resizeDividerRef = useRef(null);
  
  // Handler for row selection - fixed to properly set the selectedRow
  const handleRowSelect = (row) => {
    // Find the original item with all properties from foundItems
    const selectedItem = foundItems.find(item => item._id === row._id);
    setSelectedRow(selectedItem);
  };
  
  // Handler for checkbox toggle
  const handleCheckboxToggle = (itemId, e) => {
    e.stopPropagation(); // Prevent row selection when clicking checkbox
    if (checkedRows.includes(itemId)) {
      setCheckedRows(checkedRows.filter(id => id !== itemId));
    } else {
      setCheckedRows([...checkedRows, itemId]);
    }
  };
  
  // Custom resizing functionality
  const startResize = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const stopResize = () => {
    setIsDragging(false);
  };
  
  const resize = (e) => {
    if (isDragging && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;
      
      // Calculate percentage (constrained between 30% and 70%)
      let newPosition = (mouseX / containerWidth) * 100;
      newPosition = Math.max(30, Math.min(70, newPosition));
      
      setDividerPosition(newPosition);
    }
  };
  
  // Setup event listeners for resize
  useEffect(() => {
    const handleMouseMove = (e) => resize(e);
    const handleMouseUp = () => stopResize();
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Custom cell formatter for the checkbox column
  const checkboxFormatter = (cell, row) => {
    return (
      <input
        type="checkbox"
        checked={checkedRows.includes(row._id)}
        onChange={(e) => handleCheckboxToggle(row._id, e)}
        onClick={(e) => e.stopPropagation()}
      />
    );
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Table columns configuration
  const columns = [
    {
      dataField: 'checkbox',
      text: '',
      formatter: checkboxFormatter,
      headerStyle: { width: '50px' }
    },
    {
      dataField: '_id',
      text: 'ID',
      sort: true
    },
    {
      dataField: 'shortDescription',
      text: 'Short Description',
      sort: true
    },
    {
      dataField: 'foundByName',
      text: 'Found By',
      sort: true
    },
    {
      dataField: 'locationName',
      text: 'Location',
      sort: true
    },
    {
      dataField: 'foundDate',
      text: 'Date Found',
      formatter: (cell) => formatDate(cell),
      sort: true
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true
    }
  ];
  
  // Row events for the table
  const rowEvents = {
    onClick: (e, row) => {
      handleRowSelect(row);
    }
  };
  
  // Add dummy data field for checkbox column
  const tableData = foundItems.map(item => ({
    ...item,
    checkbox: ''
  }));
  
  // Table row style based on selection state
  const rowStyle = (row) => {
    if (selectedRow && row._id === selectedRow._id) {
      return { backgroundColor: '#e6f7ff', cursor: 'pointer' };
    }
    return { cursor: 'pointer' };
  };
  
  // Table configuration
  const tableConfig = {
    keyField: '_id',
    data: tableData,
    columns: columns,
    rowStyle: rowStyle,
    rowEvents: rowEvents,  // Added explicit rowEvents
    hover: true,
    striped: true,
    bordered: false,
    condensed: true
  };

  return (
    <div 
      ref={containerRef}
      style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        height: '100%', 
        width: '100%', 
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Table Section */}
      <div style={{ 
        width: `${dividerPosition}%`, 
        height: '100%', 
        overflow: 'auto',
        paddingRight: '5px'
      }}>
        <BootstrapTable {...tableConfig} />
      </div>
      
      {/* Resizable Divider */}
      <div 
        ref={resizeDividerRef}
        style={{
          position: 'absolute',
          left: `calc(${dividerPosition}% - 5px)`,
          top: 0,
          bottom: 0,
          width: '10px',
          backgroundColor: isDragging ? '#aaa' : '#f0f0f0',
          cursor: 'col-resize',
          zIndex: 100,
          transition: isDragging ? 'none' : 'background-color 0.2s'
        }}
        onMouseDown={startResize}
      />
      
      {/* Details Dock */}
      <div style={{ 
        width: `${100 - dividerPosition}%`, 
        height: '100%', 
        overflow: 'auto',
        padding: '16px',
        paddingLeft: '16px'
      }}>
        {selectedRow ? (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Image Section */}
            {selectedRow.url && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <img 
                  src={selectedRow.url} 
                  alt={selectedRow.shortDescription}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '300px', 
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            
            {/* Details Section */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              <h2 style={{ marginBottom: '16px' }}>{selectedRow.shortDescription}</h2>
              
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ marginBottom: '8px' }}>Details</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold', width: '120px' }}>ID:</td>
                      <td style={{ padding: '8px' }}>{selectedRow._id}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Found By:</td>
                      <td style={{ padding: '8px' }}>{selectedRow.foundByName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Location:</td>
                      <td style={{ padding: '8px' }}>{selectedRow.locationName}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Date Found:</td>
                      <td style={{ padding: '8px' }}>{formatDate(selectedRow.foundDate)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Status:</td>
                      <td style={{ padding: '8px' }}>{selectedRow.status}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Age:</td>
                      <td style={{ padding: '8px' }}>{selectedRow.age}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div>
                <h4 style={{ marginBottom: '8px' }}>Description</h4>
                <p style={{ lineHeight: '1.5' }}>{selectedRow.longDescription}</p>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            color: '#999',
            fontSize: '16px'
          }}>
            Select an item to view details
          </div>
        )}
      </div>
    </div>
  );
};

export default FoundItemsDashboard;